<template>
  <v-container fluid class="template-verification">
    <v-row class="flex-grow-0">
      <h3>Template Verification (v1.1)</h3>
      <v-spacer></v-spacer>
    </v-row>
      
      <v-row class="flex-grow-0">
        <v-col cols="12">
          <p>Upload Excel files to verify template changes and compare data with intelligent highlighting.</p>
        </v-col>
      </v-row>

      <v-row class="flex-grow-0">
        <v-col cols="12">
          <!-- File Upload Section -->
        <FileUpload
          :old-file="oldFile"
          :new-file="newFile"
          :disabled="isProcessing"
          :is-processing="isProcessing"
          :has-comparison-data="comparisonData.length > 0"
          :is-exporting="isExporting"
          @old-file-upload="handleOldFileUpload"
          @new-file-upload="handleNewFileUpload"
          @process-files="handleProcessFiles"
          @export-output="handleExportOutput"
          @reset="reset"
        />
      </v-col>
    </v-row>

    <!-- Message Section -->
    <v-row v-if="message" class="flex-grow-0">
      <v-col cols="12">
        <v-alert
          :color="message.includes('Error') ? 'error' : 'info'"
          text
          border="left"
        >
          {{ message }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Excel Files Data Display -->
    <v-row v-if="showOldFileData || showNewFileData" class="flex-grow-1">
      <v-col cols="12">
        <!-- Data Tables Side by Side -->
        <div class="tables-side-by-side">
          <div id="old-excel-section" class="table-column" v-if="showOldFileData">
            <DataTable
              title="📄 Old Excel File Data"
              :data="oldFileData"
              :headers="oldFileHeaders"
              :show-data="showOldFileData"
              :is-column-visible="isColumnVisible"
              :is-column-visible-by-name="isColumnVisibleByName"
            />
          </div>

          <div id="new-excel-section" class="table-column" v-if="showNewFileData">
            <DataTable
              title="📄 New Excel File Data"
              :data="newFileData"
              :headers="newFileHeaders"
              :show-data="showNewFileData"
              :is-column-visible="isColumnVisible"
              :is-column-visible-by-name="isColumnVisibleByName"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Comparison Output Section -->
    <v-row v-if="showComparisonOutput" class="flex-grow-1">
      <v-col cols="12">
        <OutputSection
          :comparison-data="comparisonData"
          :comparison-headers="comparisonHeaders"
          :show-comparison-output="showComparisonOutput"
          :is-column-visible="isColumnVisible"
          :is-column-visible-by-name="isColumnVisibleByName"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import FileUpload from '@/components/templateVerification/FileUpload.vue'
import DataTable from '@/components/templateVerification/DataTable.vue'
import OutputSection from '@/components/templateVerification/OutputSection.vue'
import { useExcelProcessor } from '@/composables/useExcelProcessor'
import { useColumnVisibility } from '@/composables/useColumnVisibility'

export default {
  name: "TemplateVerification",
  components: {
    FileUpload,
    DataTable,
    OutputSection
  },
  setup(props, { root }) {
    // File state
    const oldFile = ref(null)
    const newFile = ref(null)
    const oldFileData = ref([])
    const oldFileHeaders = ref([])
    const showOldFileData = ref(false)
    const newFileData = ref([])
    const newFileHeaders = ref([])
    const showNewFileData = ref(false)

    // Comparison output state
    const comparisonData = ref([])
    const comparisonHeaders = ref([])
    const showComparisonOutput = ref(false)

    // Export state
    const isExporting = ref(false)

    // Composables
    const { isProcessing, message, processExcelFile, processFiles, exportToExcel } = useExcelProcessor()
    const { 
      hiddenColumnNames, 
      initializeColumnVisibility, 
      isColumnVisible, 
      isColumnVisibleByName
    } = useColumnVisibility()

    // Filtered data counts (excluding empty rows)
    const filteredOldDataCount = computed(() => {
      return oldFileData.value.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '').length
    })

    const filteredNewDataCount = computed(() => {
      return newFileData.value.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '').length
    })

    const filteredOldData = computed(() => {
      return oldFileData.value.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '')
    })

    const filteredNewData = computed(() => {
      return newFileData.value.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '')
    })

    // File upload handlers
    const handleOldFileUpload = async (file) => {
      oldFile.value = file
      message.value = `Old file uploaded: ${file.name}. Reading data...`
      
      const result = await processExcelFile(file, true)
      
      if (result.success) {
        oldFileHeaders.value = result.headers
        oldFileData.value = result.data
        showOldFileData.value = true
        
        // Initialize column visibility if this is the first file
        if (newFileHeaders.value.length === 0) {
          initializeColumnVisibility(result.headers)
        }
        
        const filteredCount = result.data.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '').length
        message.value = `Old file processed successfully: ${filteredCount} rows loaded (${result.data.length - filteredCount} empty rows filtered out)`
      } else {
        message.value = `Error processing old file: ${result.error}`
      }
    }

    const handleNewFileUpload = async (file) => {
      newFile.value = file
      message.value = `New file uploaded: ${file.name}. Reading data...`
      
      const result = await processExcelFile(file, false)
      
      if (result.success) {
        newFileHeaders.value = result.headers
        newFileData.value = result.data
        showNewFileData.value = true
        
        // Initialize column visibility if this is the first file
        if (oldFileHeaders.value.length === 0) {
          initializeColumnVisibility(result.headers)
        }
        
        const filteredCount = result.data.filter(row => row.type && row.type !== 'default' && row.type.trim() !== '').length
        message.value = `New file processed successfully: ${filteredCount} rows loaded (${result.data.length - filteredCount} empty rows filtered out)`
      } else {
        message.value = `Error processing new file: ${result.error}`
      }
    }

    // Action handlers
    const handleProcessFiles = async () => {
      const result = await processFiles(
        oldFile.value, 
        newFile.value, 
        oldFileData.value, 
        newFileData.value,
        oldFileHeaders.value,
        newFileHeaders.value
      )
      
      if (result.success) {
        comparisonData.value = result.data
        comparisonHeaders.value = result.headers
        showComparisonOutput.value = true
      } else {
        // Handle validation errors more prominently
        if (result.missingColumns) {
          // Show detailed error message for missing comparison columns
          const errorDetails = result.missingColumns.map(error => `• ${error}`).join('\n')
          message.value = `❌ VALIDATION ERROR: Cannot generate comparison output due to missing required columns:\n\n${errorDetails}\n\nPlease ensure both Excel files contain the correct comparison columns:\n• New Excel file must have "Previous item no." column\n• Old Excel file must have "Current item no." column`
        } else {
          message.value = `❌ ERROR: ${result.error}`
        }
      }
    }

    const reset = () => {
      // Reset all state
      oldFile.value = null
      newFile.value = null
      oldFileData.value = []
      oldFileHeaders.value = []
      showOldFileData.value = false
      newFileData.value = []
      newFileHeaders.value = []
      showNewFileData.value = false
      comparisonData.value = []
      comparisonHeaders.value = []
      showComparisonOutput.value = false
      hiddenColumnNames.value = new Set()
      message.value = 'System reset. You can upload new files or reload samples.'
    }

    // Export functionality
    const handleExportOutput = async () => {
      if (!comparisonData.value || comparisonData.value.length === 0) {
        return
      }
      
      isExporting.value = true
      
      try {
        const result = await exportToExcel(comparisonData.value, comparisonHeaders.value)
        
        if (result.success) {
          message.value = '✅ Excel file exported successfully!'
          setTimeout(() => {
            message.value = ''
          }, 3000)
        } else {
          message.value = `❌ Export failed: ${result.error}`
        }
        
      } catch (error) {
        console.error('Export failed:', error)
        message.value = 'Failed to export Excel file. Please try again.'
      } finally {
        isExporting.value = false
      }
    }

    return {
      // State
      oldFile,
      newFile,
      oldFileData,
      oldFileHeaders,
      showOldFileData,
      newFileData,
      newFileHeaders,
      showNewFileData,
      comparisonData,
      comparisonHeaders,
      showComparisonOutput,
      isExporting,
      isProcessing,
      message,
      
      // Computed
      filteredOldDataCount,
      filteredNewDataCount,
      filteredOldData,
      filteredNewData,
      
      // Methods
      handleOldFileUpload,
      handleNewFileUpload,
      handleProcessFiles,
      handleExportOutput,
      reset,
      
      // Composable functions
      isColumnVisible,
      isColumnVisibleByName
    }
  }
};
</script>

<style scoped>
/* Import the template verification styles */
@import '../assets/templateVerification.css';

.template-verification-page {
  padding: 0;
  max-width: 100%;
}

.app {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
  font-family: 'Roboto', sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 30px;
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.header h1 {
  margin: 0 0 10px 0;
  font-size: 2.5rem;
  font-weight: 300;
}

.header p {
  margin: 0;
  font-size: 1.1rem;
  opacity: 0.9;
}

.main-content {
  max-width: 1400px;
  margin: 0 auto;
}

.message-section {
  margin: 20px 0;
  text-align: center;
}

.message {
  display: inline-block;
  padding: 12px 24px;
  background-color: #e3f2fd;
  border: 1px solid #2196f3;
  border-radius: 8px;
  color: #1976d2;
  font-weight: 500;
  white-space: pre-line;
}

.message.error {
  background-color: #ffebee;
  border-color: #f44336;
  color: #c62828;
}

.data-display-container {
  margin-top: 30px;
}

.section-header {
  text-align: center;
  margin-bottom: 25px;
  padding: 20px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
  border-radius: 8px;
}

.section-header h2 {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
}

.section-header p {
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
}

.tables-side-by-side {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  justify-content: space-between;
}

.table-column {
  flex: 1;
  min-width: 0;
  max-width: calc(50% - 10px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .app {
    padding: 5px;
  }
  
  .header {
    padding: 15px;
  }
  
  .header h1 {
    font-size: 1.5rem;
  }
  
  .section-header {
    padding: 15px;
  }
  
  .section-header h2 {
    font-size: 1.3rem;
  }
  
  .tables-side-by-side {
    flex-direction: column;
    gap: 15px;
  }
  
  .table-column {
    max-width: 100%;
  }
}
</style>
